import { useMemo } from 'react';
import { useSubscription } from './useSubscription';
import { useAccounts } from './useAccounts';
import { useCards } from './useCards';
import { useGoals } from './useGoals';

export interface ExceededItem {
  resource: string;
  resourcePlural: string;
  current: number;
  limit: number;
  path: string;
  icon: string;
}

export interface DowngradeAlertResult {
  hasExceeded: boolean;
  exceededItems: ExceededItem[];
}

/** Returns items that currently exceed the plan limits (downgrade scenario). */
export const useDowngradeAlert = (): DowngradeAlertResult => {
  const { plan, loading: subLoading } = useSubscription();
  const { accounts } = useAccounts();
  const { cards } = useCards();
  const { goals } = useGoals();

  const isUnlimited = (val: number | null | undefined) => val === -1 || val === null;

  const exceededItems = useMemo<ExceededItem[]>(() => {
    // Guard: never show downgrade alerts while subscription is loading
    // or when plan data is not yet available — avoids false positives
    if (subLoading || !plan) return [];

    const items: ExceededItem[] = [];

    // Accounts
    const accLimit = plan.accounts_limit ?? 1;
    if (!isUnlimited(accLimit) && accounts.length > accLimit) {
      items.push({
        resource: 'conta',
        resourcePlural: 'contas',
        current: accounts.length,
        limit: accLimit,
        path: '/contas',
        icon: 'ri-bank-line',
      });
    }

    // Cards
    const cardLimit = plan.cards_limit ?? 1;
    if (!isUnlimited(cardLimit) && cards.length > cardLimit) {
      items.push({
        resource: 'cartão',
        resourcePlural: 'cartões',
        current: cards.length,
        limit: cardLimit,
        path: '/cartoes',
        icon: 'ri-bank-card-line',
      });
    }

    // Goals
    const goalLimit = plan.goals_limit ?? 2;
    if (!isUnlimited(goalLimit) && goals.length > goalLimit) {
      items.push({
        resource: 'meta',
        resourcePlural: 'metas',
        current: goals.length,
        limit: goalLimit,
        path: '/metas',
        icon: 'ri-focus-3-line',
      });
    }

    return items;
  }, [plan, accounts, cards, goals, subLoading]);

  return {
    hasExceeded: exceededItems.length > 0,
    exceededItems,
  };
};
